'use client'

/**
 * Google Maps API Loader with singleton pattern and lazy loading.
 * Ensures Google Maps library is loaded only once and cached.
 */

let googleMapsPromise: Promise<typeof google> | null = null

export async function loadGoogleMapsAPI(): Promise<typeof google> {
  // Return cached promise if already loading or loaded
  if (googleMapsPromise) {
    return googleMapsPromise
  }

  // Assign immediately to prevent duplicate loads from concurrent calls
  googleMapsPromise = new Promise((resolve, reject) => {
    // Check if already loaded
    if (typeof window !== 'undefined' && window.google?.maps) {
      resolve(window.google)
      return
    }

    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY
    if (!apiKey) {
      googleMapsPromise = null
      reject(new Error('NEXT_PUBLIC_GOOGLE_MAPS_API_KEY is not configured'))
      return
    }

    // Use a unique callback name to avoid collisions
    const callbackName = '__initGoogleMaps_' + Date.now()

    const params = new URLSearchParams({
      key: apiKey,
      v: 'weekly',
      libraries: 'marker',
      callback: callbackName,
    })

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`
    script.async = true

    // Define global callback
    ;(window as any)[callbackName] = () => {
      delete (window as any)[callbackName]
      if (window.google?.maps) {
        resolve(window.google)
      } else {
        googleMapsPromise = null
        reject(new Error('Failed to load Google Maps API'))
      }
    }

    script.onerror = () => {
      delete (window as any)[callbackName]
      document.head.removeChild(script)
      googleMapsPromise = null
      reject(new Error('Failed to load Google Maps API script'))
    }

    document.head.appendChild(script)
  })

  return googleMapsPromise
}
